import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { AppLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Plus, Pencil, Trash2, Building2, Globe, Loader2, Sparkles, Upload, CheckCircle2, FileJson, ChevronDown, ChevronRight, Brain, Target, TrendingUp, AlertTriangle, Lightbulb, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_brandbrain-ai/artifacts/wegfskta_SILLON.png";

export default function BrandsPage() {
  const { t, language } = useLanguage();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [coreglDialogBrand, setCoreglDialogBrand] = useState(null);
  const [uploadingCoregl, setUploadingCoregl] = useState(null);
  const fileInputRef = useRef(null);
  const coreglFormInputRef = useRef(null);
  const [importingCoreglForForm, setImportingCoreglForForm] = useState(false);
  const [coreglImportedData, setCoreglImportedData] = useState(null);
  const [fieldsFromCoregl, setFieldsFromCoregl] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    website_url: '',
    target: '',
    brand_promise: '',
    services: '',
    slogan: '',
    tone: '',
    keywords: '',
    extra_info: '',
    logo_url: ''
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`, { withCredentials: true });
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error(language === 'es' ? 'Error al cargar marcas' : 'Error loading brands');
    } finally {
      setLoading(false);
    }
  };

  const handleCoreglFileSelect = (brandId) => {
    setUploadingCoregl(brandId);
    fileInputRef.current?.click();
  };

  const handleCoreglUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !uploadingCoregl) return;

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      
      const response = await axios.post(
        `${API}/brands/${uploadingCoregl}/coregl`,
        { coregl_data: jsonData },
        { withCredentials: true }
      );
      
      // Update the brand in local state
      setBrands(prev => prev.map(brand => 
        brand.brand_id === uploadingCoregl 
          ? { 
              ...brand, 
              coregl_data: jsonData,
              coregl_connected: true,
              coregl_updated_at: response.data.coregl_updated_at
            }
          : brand
      ));
      
      toast.success(language === 'es' ? 'CORE-GL conectado exitosamente' : 'CORE-GL connected successfully');
      
    } catch (error) {
      console.error('Error uploading CORE-GL:', error);
      const errorMsg = error.response?.data?.detail || 
        (language === 'es' 
          ? 'Archivo no compatible. Exporta desde CORE-GL e intenta de nuevo.' 
          : 'Incompatible file. Export from CORE-GL and try again.');
      toast.error(errorMsg);
    } finally {
      setUploadingCoregl(null);
      event.target.value = '';
    }
  };

  const handleRemoveCoregl = async (brandId) => {
    try {
      await axios.delete(`${API}/brands/${brandId}/coregl`, { withCredentials: true });
      
      setBrands(prev => prev.map(brand => 
        brand.brand_id === brandId 
          ? { ...brand, coregl_data: null, coregl_connected: false, coregl_updated_at: null }
          : brand
      ));
      
      toast.success(language === 'es' ? 'CORE-GL desconectado' : 'CORE-GL disconnected');
    } catch (error) {
      console.error('Error removing CORE-GL:', error);
      toast.error(language === 'es' ? 'Error al desconectar' : 'Error disconnecting');
    }
  };

  const handleCoreglImportForForm = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportingCoreglForForm(true);
    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      
      // Validate required structure
      if (!jsonData.executive_summary || !jsonData.brand_personality_input) {
        toast.error(language === 'es' 
          ? 'Archivo no compatible. Exporta desde CORE-GL e intenta de nuevo.' 
          : 'Incompatible file. Export from CORE-GL and try again.');
        return;
      }

      // Extract data for form auto-fill
      const metadata = jsonData.metadata || {};
      const execSummary = jsonData.executive_summary || {};
      const brandInput = jsonData.brand_personality_input || {};
      const buyerPersona = brandInput.buyer_persona || {};
      const blueOcean = brandInput.blue_ocean_opportunities || [];
      // communication_tones is now an array - read first element
      const commTonesArray = brandInput.comparative_analysis?.communication_tones || [];
      const commTones = commTonesArray[0] || {};
      
      // Build target from buyer persona
      const targetParts = [];
      if (buyerPersona.name) targetParts.push(buyerPersona.name);
      if (buyerPersona.age) targetParts.push(`${buyerPersona.age} años`);
      if (buyerPersona.occupation) targetParts.push(buyerPersona.occupation);
      const targetValue = targetParts.join(', ');
      
      // Extract tone from first element of communication_tones array
      const toneValue = commTones.tone_description || '';
      
      // Build keywords from blue ocean suggested messages
      const keywordsValue = blueOcean
        .map(opp => opp.suggested_message)
        .filter(Boolean)
        .slice(0, 5)
        .join(', ');
      
      // Build extra info from executive summary
      const extraInfoParts = [];
      if (execSummary.main_finding) extraInfoParts.push(`Hallazgo: ${execSummary.main_finding}`);
      if (execSummary.urgent_action) extraInfoParts.push(`Acción urgente: ${execSummary.urgent_action}`);
      const extraInfoValue = extraInfoParts.join('\n');
      
      // Track which fields are from CORE-GL
      const newFieldsFromCoregl = new Set();
      
      // Update form data (never auto-fill name and slogan)
      setFormData(prev => {
        const updates = { ...prev };
        
        if (metadata.source_url) {
          updates.website_url = metadata.source_url;
          newFieldsFromCoregl.add('website_url');
        }
        if (targetValue) {
          updates.target = targetValue;
          newFieldsFromCoregl.add('target');
        }
        if (execSummary.key_opportunity) {
          updates.brand_promise = execSummary.key_opportunity;
          newFieldsFromCoregl.add('brand_promise');
        }
        if (toneValue) {
          updates.tone = toneValue;
          newFieldsFromCoregl.add('tone');
        }
        if (keywordsValue) {
          updates.keywords = keywordsValue;
          newFieldsFromCoregl.add('keywords');
        }
        if (extraInfoValue) {
          updates.extra_info = extraInfoValue;
          newFieldsFromCoregl.add('extra_info');
        }
        
        return updates;
      });
      
      setFieldsFromCoregl(newFieldsFromCoregl);
      setCoreglImportedData(jsonData);
      
      toast.success(language === 'es' ? 'CORE-GL importado ✓' : 'CORE-GL imported ✓');
      
    } catch (error) {
      console.error('Error importing CORE-GL:', error);
      toast.error(language === 'es' 
        ? 'Archivo no compatible. Exporta desde CORE-GL e intenta de nuevo.' 
        : 'Incompatible file. Export from CORE-GL and try again.');
    } finally {
      setImportingCoreglForForm(false);
      event.target.value = '';
    }
  };

  const handleExtractFromUrl = async () => {
    if (!formData.website_url) {
      toast.error(language === 'es' ? 'Ingresa una URL' : 'Enter a URL');
      return;
    }

    setExtracting(true);
    try {
      const response = await axios.post(`${API}/extract-website`, 
        { url: formData.website_url },
        { withCredentials: true }
      );
      
      const extracted = response.data;
      setFormData(prev => ({
        ...prev,
        name: extracted.name || prev.name,
        slogan: extracted.slogan || prev.slogan,
        brand_promise: extracted.brand_promise || prev.brand_promise,
        services: extracted.services || prev.services,
        target: extracted.target || prev.target,
        tone: extracted.tone || prev.tone,
        keywords: extracted.keywords || prev.keywords,
      }));
      
      toast.success(language === 'es' ? '¡Información extraída!' : 'Information extracted!');
    } catch (error) {
      console.error('Error extracting:', error);
      toast.error(language === 'es' ? 'Error al extraer información' : 'Error extracting information');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await axios.put(`${API}/brands/${editingBrand.brand_id}`, formData, { withCredentials: true });
        toast.success(language === 'es' ? 'Marca actualizada' : 'Brand updated');
      } else {
        // Create the brand first
        const createResponse = await axios.post(`${API}/brands`, formData, { withCredentials: true });
        const newBrandId = createResponse.data.brand_id;
        
        // If CORE-GL data was imported, save it to the new brand
        if (coreglImportedData && newBrandId) {
          try {
            await axios.post(
              `${API}/brands/${newBrandId}/coregl`,
              { coregl_data: coreglImportedData },
              { withCredentials: true }
            );
          } catch (coreglError) {
            console.error('Error saving CORE-GL data:', coreglError);
            // Don't fail the whole operation if CORE-GL save fails
          }
        }
        
        toast.success(language === 'es' ? 'Marca creada' : 'Brand created');
      }
      setIsDialogOpen(false);
      setEditingBrand(null);
      resetForm();
      setCoreglImportedData(null);
      setFieldsFromCoregl(new Set());
      fetchBrands();
    } catch (error) {
      console.error('Error saving brand:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name || '',
      website_url: brand.website_url || '',
      target: brand.target || '',
      brand_promise: brand.brand_promise || '',
      services: brand.services || '',
      slogan: brand.slogan || '',
      tone: brand.tone || '',
      keywords: brand.keywords || '',
      extra_info: brand.extra_info || '',
      logo_url: brand.logo_url || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/brands/${deleteTarget.brand_id}`, { withCredentials: true });
      toast.success(language === 'es' ? 'Marca eliminada' : 'Brand deleted');
      setDeleteTarget(null);
      fetchBrands();
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      website_url: '',
      target: '',
      brand_promise: '',
      services: '',
      slogan: '',
      tone: '',
      keywords: '',
      extra_info: '',
      logo_url: ''
    });
    setCoreglImportedData(null);
    setFieldsFromCoregl(new Set());
  };

  const openNewDialog = () => {
    setEditingBrand(null);
    resetForm();
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-lime-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in" data-testid="brands-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {t('myBrands')}
            </h1>
            <p className="text-zinc-400 mt-1">
              {language === 'es' ? 'Gestiona la información de tus marcas y clientes' : 'Manage your brands and clients information'}
            </p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={openNewDialog}
                className="bg-lime-500 text-black hover:bg-lime-600 font-medium gap-2"
                data-testid="add-brand-btn"
              >
                <Plus className="w-4 h-4" />
                {t('addBrand')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {editingBrand ? t('editBrand') : t('addBrand')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Import from CORE-GL */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => coreglFormInputRef.current?.click()}
                    disabled={importingCoreglForForm}
                    className="flex-1 border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300 gap-2"
                    data-testid="import-coregl-form-btn"
                  >
                    {importingCoreglForForm ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileJson className="w-4 h-4" />
                    )}
                    {language === 'es' ? 'Importar desde CORE-GL' : 'Import from CORE-GL'}
                  </Button>
                  {coreglImportedData && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-md">
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-purple-300">CORE-GL ✓</span>
                    </div>
                  )}
                </div>

                {/* Website URL with Extract */}
                <div className="space-y-2 p-4 bg-lime-500/5 border border-lime-500/20 rounded-lg">
                  <Label className="text-xs font-medium uppercase text-lime-500">
                    {language === 'es' ? 'Extraer desde sitio web' : 'Extract from website'}
                  </Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        value={formData.website_url}
                        onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                        placeholder="https://www.ejemplo.com"
                        className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500 pl-10"
                        data-testid="brand-url-input"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleExtractFromUrl}
                      disabled={extracting || !formData.website_url}
                      className="bg-lime-500 text-black hover:bg-lime-600 font-medium gap-2"
                      data-testid="extract-btn"
                    >
                      {extracting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      {language === 'es' ? 'Extraer' : 'Extract'}
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500">
                    {language === 'es' 
                      ? 'La IA extraerá automáticamente la información institucional'
                      : 'AI will automatically extract institutional information'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase text-zinc-500">{t('brandName')} *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Nike, Apple, etc."
                      required
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500"
                      data-testid="brand-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase text-zinc-500">{t('slogan')}</Label>
                    <Input
                      value={formData.slogan}
                      onChange={(e) => setFormData({...formData, slogan: e.target.value})}
                      placeholder="Just Do It"
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500"
                      data-testid="brand-slogan-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-zinc-500">{t('target')}</Label>
                  <Textarea
                    value={formData.target}
                    onChange={(e) => setFormData({...formData, target: e.target.value})}
                    placeholder={language === 'es' ? 'Jóvenes 18-35 años, amantes del deporte...' : 'Young adults 18-35, sports lovers...'}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500 min-h-[80px]"
                    data-testid="brand-target-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-zinc-500">{t('brandPromise')}</Label>
                  <Textarea
                    value={formData.brand_promise}
                    onChange={(e) => setFormData({...formData, brand_promise: e.target.value})}
                    placeholder={language === 'es' ? 'Ofrecer productos de calidad que...' : 'Provide quality products that...'}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500 min-h-[80px]"
                    data-testid="brand-promise-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-zinc-500">{t('services')}</Label>
                  <Textarea
                    value={formData.services}
                    onChange={(e) => setFormData({...formData, services: e.target.value})}
                    placeholder={language === 'es' ? 'Zapatos deportivos, ropa, accesorios...' : 'Sports shoes, clothing, accessories...'}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500 min-h-[80px]"
                    data-testid="brand-services-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase text-zinc-500">{t('tone')}</Label>
                    <Input
                      value={formData.tone}
                      onChange={(e) => setFormData({...formData, tone: e.target.value})}
                      placeholder={language === 'es' ? 'Motivacional, energético...' : 'Motivational, energetic...'}
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500"
                      data-testid="brand-tone-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase text-zinc-500">{t('keywords')}</Label>
                    <Input
                      value={formData.keywords}
                      onChange={(e) => setFormData({...formData, keywords: e.target.value})}
                      placeholder={language === 'es' ? 'innovación, calidad, pasión' : 'innovation, quality, passion'}
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500"
                      data-testid="brand-keywords-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-zinc-500">{t('extraInfo')}</Label>
                  <Textarea
                    value={formData.extra_info}
                    onChange={(e) => setFormData({...formData, extra_info: e.target.value})}
                    placeholder={language === 'es' ? 'Cualquier información adicional relevante...' : 'Any additional relevant information...'}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500 min-h-[60px]"
                    data-testid="brand-extra-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsDialogOpen(false)}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    {t('cancel')}
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-lime-500 text-black hover:bg-lime-600 font-medium"
                    data-testid="save-brand-btn"
                  >
                    {t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Brands Grid */}
        {brands.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <img src={MASCOT_URL} alt="No brands" className="w-40 h-40 object-contain opacity-50 mb-6" />
            <p className="text-zinc-400 text-center mb-4">{t('noBrands')}</p>
            <Button 
              onClick={openNewDialog}
              className="bg-lime-500 text-black hover:bg-lime-600 font-medium gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('createFirst')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {brands.map((brand) => (
              <Card 
                key={brand.brand_id}
                className="bg-zinc-900/50 border-zinc-800 hover:border-lime-500/30 transition-all group"
                data-testid={`brand-card-${brand.brand_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-red-500" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-white">{brand.name}</CardTitle>
                        {brand.slogan && (
                          <p className="text-xs text-zinc-500 italic">"{brand.slogan}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEdit(brand)}
                        className="h-8 w-8 text-zinc-400 hover:text-lime-500 hover:bg-zinc-800"
                        data-testid={`edit-brand-${brand.brand_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setDeleteTarget(brand)}
                        className="h-8 w-8 text-zinc-400 hover:text-red-500 hover:bg-zinc-800"
                        data-testid={`delete-brand-${brand.brand_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {/* CORE-GL Badge */}
                  {brand.coregl_connected && (
                    <Badge 
                      className="mt-2 bg-green-500/20 text-green-400 border-green-500/30 cursor-pointer hover:bg-green-500/30"
                      onClick={() => setCoreglDialogBrand(brand)}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      CORE-GL conectado
                    </Badge>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  {brand.website_url && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Globe className="w-3 h-3" />
                      <a href={brand.website_url} target="_blank" rel="noopener noreferrer" className="hover:text-lime-500 truncate">
                        {brand.website_url}
                      </a>
                    </div>
                  )}
                  {brand.target && (
                    <div>
                      <p className="text-xs font-medium uppercase text-zinc-500">{t('target')}</p>
                      <p className="text-sm text-zinc-300 line-clamp-2">{brand.target}</p>
                    </div>
                  )}
                  {brand.services && (
                    <div>
                      <p className="text-xs font-medium uppercase text-zinc-500">{t('services')}</p>
                      <p className="text-sm text-zinc-300 line-clamp-2">{brand.services}</p>
                    </div>
                  )}
                  {brand.tone && (
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-lime-500/10 text-lime-500 text-xs rounded-full border border-lime-500/20">
                        {brand.tone}
                      </span>
                    </div>
                  )}
                  
                  {/* CORE-GL Intelligence Card */}
                  <div className="mt-3 p-3 bg-gradient-to-br from-purple-900/20 to-zinc-900/30 border border-purple-500/30 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-purple-300">Inteligencia CORE-GL</span>
                      </div>
                    </div>
                    
                    {brand.coregl_connected ? (
                      <div className="space-y-2">
                        <p className="text-xs text-zinc-400">
                          {language === 'es' ? 'Análisis conectado el ' : 'Analysis connected on '}
                          {new Date(brand.coregl_updated_at).toLocaleDateString()}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCoreglDialogBrand(brand)}
                            className="h-7 text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/10 flex-1"
                          >
                            Ver análisis
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCoreglFileSelect(brand.brand_id)}
                            className="h-7 text-xs border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                          >
                            <Upload className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCoreglFileSelect(brand.brand_id)}
                        disabled={uploadingCoregl === brand.brand_id}
                        className="w-full h-8 text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      >
                        {uploadingCoregl === brand.brand_id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-1" />
                        )}
                        Importar análisis CORE-GL
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">
                {language === 'es' ? '¿Eliminar marca?' : 'Delete brand?'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                {language === 'es' 
                  ? `Esta acción eliminará "${deleteTarget?.name}" y no se puede deshacer.`
                  : `This action will delete "${deleteTarget?.name}" and cannot be undone.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Hidden File Input for CORE-GL (existing brand) */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleCoreglUpload}
          className="hidden"
        />

        {/* Hidden File Input for CORE-GL (new brand form) */}
        <input
          ref={coreglFormInputRef}
          type="file"
          accept=".json"
          onChange={handleCoreglImportForForm}
          className="hidden"
        />

        {/* CORE-GL Details Dialog */}
        <Dialog open={!!coreglDialogBrand} onOpenChange={() => setCoreglDialogBrand(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-400" />
                Inteligencia CORE-GL - {coreglDialogBrand?.name}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {language === 'es' 
                  ? 'Análisis estratégico de marca. Solo lectura.' 
                  : 'Strategic brand analysis. Read-only.'}
              </DialogDescription>
            </DialogHeader>
            
            {coreglDialogBrand?.coregl_data && (() => {
              const data = coreglDialogBrand.coregl_data;
              const metadata = data.metadata || {};
              const execSummary = data.executive_summary || {};
              const brandInput = data.brand_personality_input || {};
              const buyerPersona = brandInput.buyer_persona || {};
              const psycho = buyerPersona.psychographic_profile || {};
              const blueOcean = brandInput.blue_ocean_opportunities || [];
              // SWOT is now optional in newer CORE-GL exports
              const swot = brandInput.comparative_analysis?.swot_analysis || null;
              const recommendations = brandInput.strategic_recommendations || [];
              
              return (
                <ScrollArea className="flex-1 pr-4">
                  <div className="space-y-4 pb-4">
                    {/* Metadata */}
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                          <span className="text-zinc-500 uppercase">Marca analizada</span>
                          <p className="text-white truncate">{metadata.source_url || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-zinc-500 uppercase">Mercado</span>
                          <p className="text-white">{metadata.location || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-zinc-500 uppercase">Fecha análisis</span>
                          <p className="text-white">{metadata.created_at ? new Date(metadata.created_at).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Executive Summary */}
                    <Collapsible defaultOpen>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg hover:bg-yellow-500/20 transition-colors">
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-yellow-400" />
                          <span className="text-sm font-medium text-yellow-300">Resumen Ejecutivo</span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-yellow-400" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2 p-3 bg-zinc-800/50 rounded-lg space-y-3">
                        {execSummary.main_finding && (
                          <div>
                            <span className="text-xs text-zinc-500 uppercase">Hallazgo Principal</span>
                            <p className="text-sm text-white">{execSummary.main_finding}</p>
                          </div>
                        )}
                        {execSummary.competitive_position && (
                          <div>
                            <span className="text-xs text-zinc-500 uppercase">Posición Competitiva</span>
                            <p className="text-sm text-zinc-300">{execSummary.competitive_position}</p>
                          </div>
                        )}
                        {execSummary.key_opportunity && (
                          <div>
                            <span className="text-xs text-zinc-500 uppercase">Oportunidad Clave</span>
                            <p className="text-sm text-lime-400">{execSummary.key_opportunity}</p>
                          </div>
                        )}
                        {execSummary.urgent_action && (
                          <div>
                            <span className="text-xs text-zinc-500 uppercase">Acción Urgente</span>
                            <p className="text-sm text-red-400">{execSummary.urgent_action}</p>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Buyer Persona */}
                    <Collapsible defaultOpen>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg hover:bg-purple-500/20 transition-colors">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-purple-400" />
                          <span className="text-sm font-medium text-purple-300">Buyer Persona</span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-purple-400" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2 p-3 bg-zinc-800/50 rounded-lg space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="text-lg font-semibold text-white">{buyerPersona.name || 'N/A'}</p>
                            {buyerPersona.tagline && <p className="text-sm text-zinc-400 italic">"{buyerPersona.tagline}"</p>}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-xs">
                          <div className="p-2 bg-zinc-900/50 rounded">
                            <span className="text-zinc-500 uppercase">Edad</span>
                            <p className="text-white">{buyerPersona.age || 'N/A'}</p>
                          </div>
                          <div className="p-2 bg-zinc-900/50 rounded">
                            <span className="text-zinc-500 uppercase">Ocupación</span>
                            <p className="text-white">{buyerPersona.occupation || 'N/A'}</p>
                          </div>
                          <div className="p-2 bg-zinc-900/50 rounded">
                            <span className="text-zinc-500 uppercase">NSE</span>
                            <p className="text-white">
                              {typeof buyerPersona.socioeconomic_level === 'object' 
                                ? (buyerPersona.socioeconomic_level?.nse_category || 'N/A')
                                : (buyerPersona.socioeconomic_level || 'N/A')}
                            </p>
                          </div>
                        </div>
                        {psycho.fears && psycho.fears.length > 0 && (
                          <div>
                            <span className="text-xs text-red-400 uppercase">Miedos</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {psycho.fears.map((fear, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] border-red-500/30 text-red-300">{fear}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {psycho.desires && psycho.desires.length > 0 && (
                          <div>
                            <span className="text-xs text-green-400 uppercase">Deseos</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {psycho.desires.map((desire, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] border-green-500/30 text-green-300">{desire}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {psycho.pain_points && psycho.pain_points.length > 0 && (
                          <div>
                            <span className="text-xs text-orange-400 uppercase">Puntos de Dolor</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {psycho.pain_points.map((point, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] border-orange-500/30 text-orange-300">{point}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Blue Ocean Opportunities */}
                    {blueOcean.length > 0 && (
                      <Collapsible defaultOpen>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-colors">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-blue-400" />
                            <span className="text-sm font-medium text-blue-300">Océano Azul ({blueOcean.length})</span>
                          </div>
                          <ChevronDown className="w-4 h-4 text-blue-400" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2 space-y-2">
                          {blueOcean.map((opp, index) => (
                            <div key={index} className="p-3 bg-zinc-800/50 rounded-lg border-l-2 border-blue-500">
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-white">{opp.gap_title}</p>
                                {opp.opportunity_score && (
                                  <Badge className="bg-blue-500/20 text-blue-300 text-[10px]">
                                    Score: {opp.opportunity_score}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-zinc-400 mt-1">{opp.gap_description}</p>
                              {opp.suggested_message && (
                                <p className="text-xs text-blue-400 mt-2 italic">→ "{opp.suggested_message}"</p>
                              )}
                              <div className="flex gap-2 mt-2 text-[10px] text-zinc-500">
                                {opp.implementation_difficulty && <span>Dificultad: {opp.implementation_difficulty}</span>}
                                {opp.potential_impact && <span>| Impacto: {opp.potential_impact}</span>}
                              </div>
                            </div>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* FODA - Only show if swot_analysis exists */}
                    {swot && (
                      <Collapsible defaultOpen>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                            <span className="text-sm font-medium text-red-300">Análisis FODA</span>
                          </div>
                          <ChevronDown className="w-4 h-4 text-red-400" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2 p-3 bg-zinc-800/50 rounded-lg">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="p-2 bg-green-500/10 border border-green-500/20 rounded">
                              <p className="text-xs text-green-500 uppercase font-medium mb-2">Fortalezas</p>
                              <ul className="text-xs text-zinc-300 space-y-1">
                                {(swot.strengths || []).map((s, i) => <li key={i}>• {s}</li>)}
                              </ul>
                            </div>
                            <div className="p-2 bg-red-500/10 border border-red-500/20 rounded">
                              <p className="text-xs text-red-500 uppercase font-medium mb-2">Debilidades</p>
                              <ul className="text-xs text-zinc-300 space-y-1">
                                {(swot.weaknesses || []).map((w, i) => <li key={i}>• {w}</li>)}
                              </ul>
                            </div>
                            <div className="p-2 bg-blue-500/10 border border-blue-500/20 rounded">
                              <p className="text-xs text-blue-500 uppercase font-medium mb-2">Oportunidades</p>
                              <ul className="text-xs text-zinc-300 space-y-1">
                                {(swot.opportunities || []).map((o, i) => <li key={i}>• {o}</li>)}
                              </ul>
                            </div>
                            <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded">
                              <p className="text-xs text-yellow-500 uppercase font-medium mb-2">Amenazas</p>
                              <ul className="text-xs text-zinc-300 space-y-1">
                                {(swot.threats || []).map((t, i) => <li key={i}>• {t}</li>)}
                              </ul>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* Strategic Recommendations */}
                    {recommendations.length > 0 && (
                      <Collapsible>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-lime-500/10 border border-lime-500/30 rounded-lg hover:bg-lime-500/20 transition-colors">
                          <div className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-lime-400" />
                            <span className="text-sm font-medium text-lime-300">Recomendaciones Estratégicas ({recommendations.length})</span>
                          </div>
                          <ChevronDown className="w-4 h-4 text-lime-400" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2 space-y-2">
                          {recommendations.map((rec, index) => (
                            <div key={index} className="p-3 bg-zinc-800/50 rounded-lg">
                              <div className="flex items-start justify-between">
                                <p className="text-sm text-white">{rec.recommendation}</p>
                                {rec.priority && (
                                  <Badge className={`text-[10px] ${
                                    rec.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                                    rec.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                                    'bg-zinc-500/20 text-zinc-300'
                                  }`}>
                                    {rec.priority}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex gap-3 mt-2 text-[10px] text-zinc-500">
                                {rec.timeframe && <span>Plazo: {rec.timeframe}</span>}
                                {rec.expected_impact && <span>| Impacto: {rec.expected_impact}</span>}
                              </div>
                            </div>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                </ScrollArea>
              );
            })()}

            <div className="flex justify-between pt-4 border-t border-zinc-800">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleRemoveCoregl(coreglDialogBrand?.brand_id);
                  setCoreglDialogBrand(null);
                }}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Desconectar CORE-GL
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCoreglFileSelect(coreglDialogBrand?.brand_id)}
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              >
                <Upload className="w-4 h-4 mr-1" />
                Reemplazar análisis
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
